import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Loader2, RefreshCw } from 'lucide-react';
import api from '../../../services/api';
import { biddingTypeLabel, fmtBRL, googleStatusLabel } from './rmfLabels';

interface BiddingReportRow {
  kind: 'portfolio' | 'standard';
  id?: string;
  name: string | null;
  type: string;
  status: string | null;
  campaigns?: number;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  all_conversions: number;
  average_cpc: number | null;
  cost_per_conversion: number | null;
}

interface Props {
  /** resource_name do portfólio que a campanha usa (customers/X/biddingStrategies/Y), se usar. */
  portfolioResource?: string | null;
  portfolioName?: string | null;
  /** Tipo da estratégia da campanha (enum do Google), para achar a linha da estratégia padrão. */
  biddingStrategyType?: string | null;
}

/** Desempenho da estratégia de lance desta campanha (últimos 30 dias, RMF R.130). Só leitura. */
const GoogleBiddingReport: React.FC<Props> = ({ portfolioResource, portfolioName, biddingStrategyType }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<BiddingReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/campaigns/google/bidding-report');
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'erro');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const portfolioId = portfolioResource ? String(portfolioResource).split('/').pop() : null;
  const matching = rows.filter((r) => {
    if (portfolioResource) {
      return r.kind === 'portfolio' && ((portfolioId && String(r.id) === portfolioId) || (!!portfolioName && r.name === portfolioName));
    }
    return r.kind === 'standard' && !!biddingStrategyType && String(r.type).toUpperCase() === String(biddingStrategyType).toUpperCase();
  });

  const num = (v: number | null | undefined, digits = 0) =>
    v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: digits });

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary-500" />
          {t('campaigns.rmf.bidReportTitle', 'Desempenho da estratégia de lance')}
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
            {t('campaigns.rmf.last30', '(últimos 30 dias)')}
          </span>
        </h4>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
          title={t('campaigns.rmf.refresh', 'Atualizar')}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('campaigns.rmf.bidReportLoading', 'Carregando desempenho...')}
        </div>
      ) : error ? (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : matching.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('campaigns.rmf.bidReportEmpty', 'Sem dados desta estratégia no período.')}
        </p>
      ) : (
        <div className="space-y-3">
          {matching.map((r, i) => (
            <div key={`${r.kind}-${r.id || r.type}-${i}`}>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {r.kind === 'portfolio'
                  ? t('campaigns.rmf.bidReportPortfolio', 'Portfólio: {{name}}', { name: r.name || '—' })
                  : t('campaigns.rmf.bidReportStandard', 'Estratégia padrão: soma das {{n}} campanha(s) da conta com este tipo', { n: r.campaigns ?? 1 })}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {[
                  { label: t('campaigns.rmf.type', 'Tipo'), value: biddingTypeLabel(t, r.type) },
                  { label: t('campaigns.rmf.status', 'Status'), value: r.kind === 'portfolio' ? googleStatusLabel(t, r.status) : '—' },
                  { label: t('campaigns.rmf.clicks', 'Cliques'), value: num(r.clicks) },
                  { label: t('campaigns.rmf.impressions', 'Impressões'), value: num(r.impressions) },
                  { label: t('campaigns.rmf.cost', 'Custo'), value: fmtBRL(r.cost) },
                  { label: t('campaigns.rmf.conversions', 'Conversões'), value: num(r.conversions, 1) },
                  { label: t('campaigns.rmf.costPerConv', 'Custo/conv.'), value: fmtBRL(r.cost_per_conversion) },
                  { label: t('campaigns.rmf.avgCpc', 'CPC médio'), value: fmtBRL(r.average_cpc) },
                ].map((m) => (
                  <div key={m.label} className="px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400">{m.label}</div>
                    <div className="font-semibold text-gray-800 dark:text-gray-200 truncate">{m.value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GoogleBiddingReport;
